import React from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Button,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import StoreIcon from "@mui/icons-material/StorefrontOutlined";
import { Company } from "src/interfaces/company";
import CompanyItem from "./companyItem";
import EmptyState from "src/components/EmptyState";

interface CompanyProps {
  count: number;
  onClickItem: (item: any) => void;
  companies: Company[];
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (page: number) => void;
  page: number;
  rowsPerPage: number;
  onClickCreate: () => void;
  onClickDelete: (item: Company) => void;
  onClickDisable: (item: Company) => void;
  onUpdateSubscription: (id: string, data: any) => Promise<any>;
}

const HEADERS = ["Company", "Country", "Type", "Branches", "Plan", "Status", ""];

const CompanyLayout = ({
  count,
  companies,
  onPageChange,
  onRowsPerPageChange,
  page,
  rowsPerPage,
  onClickCreate,
  onClickItem,
  onClickDelete,
  onClickDisable,
  onUpdateSubscription,
}: CompanyProps) => {
  return (
    <Box display="flex" flexDirection="column" gap={3}>
      {/* Page header */}
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: "#EEF2FF",
              color: "#4338CA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <StoreIcon />
          </Box>
          <Box>
            <Typography fontSize="22px" fontWeight={800} letterSpacing="-0.5px">
              Companies
            </Typography>
            <Typography fontSize="13px" color="text.secondary">
              {count} registered {count === 1 ? "company" : "companies"}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onClickCreate}
          sx={{ height: 42, px: 2.5, borderRadius: "12px", fontWeight: 700, textTransform: "none", boxShadow: "0 6px 16px rgba(99,102,241,0.25)" }}
        >
          Create Company
        </Button>
      </Box>

      <Card
        variant="outlined"
        sx={{
          borderRadius: "18px",
          borderColor: "#EEF1F5",
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(16,24,40,0.04)",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ "& th": { bgcolor: "#FAFBFC", borderBottom: "1px solid #EEF1F5", py: 1.5 } }}>
                {HEADERS.map((h, i) => (
                  <TableCell key={i} align={i === HEADERS.length - 1 ? "right" : "left"}>
                    <Typography fontSize="11px" fontWeight={800} color="text.secondary" textTransform="uppercase" letterSpacing="0.06em">
                      {h}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!companies || companies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={HEADERS.length} sx={{ border: 0 }}>
                    <EmptyState
                      icon={<StoreIcon />}
                      title="No companies yet"
                      description="Companies will appear here once they sign up, or create one manually."
                      action={{ label: "Create Company", onClick: onClickCreate }}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                companies.map((item) => (
                  <CompanyItem
                    key={item.company_uuid}
                    item={item}
                    onClickItem={onClickItem}
                    onClickDelete={onClickDelete}
                    onClickDisable={onClickDisable}
                    onUpdateSubscription={onUpdateSubscription}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ borderColor: "#EEF1F5" }} />

        <Box sx={{ px: 2, py: 0.5, display: "flex", justifyContent: "flex-end" }}>
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 25, 50]}
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => onPageChange(p)}
            onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default CompanyLayout;
